import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_OPTIONS } from "../components/Logo";
import useSeo from "../hooks/useSeo";

export default function LogoOptions() {
  useSeo({
    title: "Fresh Earth · Logo Options",
    description: "Six candidate marks for the Fresh Earth identity.",
    path: "/logos",
  });
  return (
    <div className="min-h-screen bg-bone text-ink" data-testid="logo-options-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="flex items-center justify-between mb-14">
          <div>
            <div className="text-xs tracking-[0.22em] uppercase text-moss font-semibold">Identity study</div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight mt-3">Logo options</h1>
            <p className="text-slate mt-3 max-w-xl">Six candidate marks — each holds up on a business card, a tender kit and a full-bleed hero. Pick the ones you'd like to explore further.</p>
          </div>
          <Link to="/" data-testid="logos-back" className="inline-flex items-center gap-2 rounded-full border border-stone/30 px-5 py-3 text-sm hover:bg-sand transition-colors duration-300">
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {LOGO_OPTIONS.map(({ id, Component, name, note }) => (
            <div key={id} data-testid={`logo-option-${id}`} className="rounded-3xl border border-stone/20 overflow-hidden">
              {/* Light presentation */}
              <div className="bg-sand p-10 flex items-center justify-center min-h-[220px]">
                <Component size={id === "seal" ? 180 : id === "monogram" ? 140 : 280} tone="dark" />
              </div>
              {/* Dark presentation */}
              <div className="bg-forest p-8 flex items-center justify-center min-h-[160px]">
                <Component size={id === "seal" ? 130 : id === "monogram" ? 100 : 220} tone="light" />
              </div>
              <div className="p-6 flex items-start justify-between gap-4 bg-bone">
                <div>
                  <div className="text-xs tracking-[0.16em] uppercase text-moss font-semibold">{id}</div>
                  <div className="font-serif text-2xl mt-1">{name}</div>
                  <div className="text-sm text-slate mt-2">{note}</div>
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-stone whitespace-nowrap self-end">SVG · scalable</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-sand border border-stone/20 p-8 md:p-10">
          <div className="text-xs tracking-[0.22em] uppercase text-terracotta font-semibold">Notes</div>
          <ul className="mt-4 space-y-2 text-slate leading-relaxed">
            <li>· All marks use the existing palette (forest #1a2b22, moss, terracotta, bone).</li>
            <li>· Every option is delivered as inline SVG — infinitely scalable, no external asset.</li>
            <li>· Any of these can be dropped into the Navbar in one line — happy to wire whichever you pick.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
